import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AuthShell, { AuthLabel, AuthInput, AuthPrimaryButton, AuthErrorLine, GoogleSignInButton, formatApiErrorDetail } from "../components/AuthShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("US");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, {
        email, password, name,
        phone,
        address: { line1, line2: line2 || null, city, state, postal_code: postal, country },
      }, { withCredentials: true });
      setUser(data);
      const fromCart = typeof window !== "undefined" && sessionStorage.getItem("wiu_reopen_cart") === "1";
      navigate(fromCart ? "/" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const complete = name && email && password.length >= 8 && phone && line1 && city && state && postal && country;

  return (
    <AuthShell
      eyebrow="CUSTOMER · CREATE ACCOUNT"
      title="Join the batch."
      subtitle="One account. Faster checkout. Track shipments. Leave verified-buyer reviews."
      footer={
        <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/60">
          ALREADY HAVE ONE? <Link to="/login" className="text-rose hover:text-off-white" data-testid="link-login">SIGN IN →</Link>
        </p>
      }
    >
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        <div className="border-b border-off-white/10 pb-4 space-y-4">
          <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// YOUR DETAILS</p>
          <div>
            <AuthLabel>FULL NAME</AuthLabel>
            <AuthInput type="text" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
          </div>
          <div>
            <AuthLabel>EMAIL</AuthLabel>
            <AuthInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
          </div>
          <div>
            <AuthLabel>PASSWORD (MIN 8)</AuthLabel>
            <AuthInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="register-password-input" />
          </div>
          <div>
            <AuthLabel>PHONE</AuthLabel>
            <AuthInput type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="register-phone-input" />
          </div>
        </div>

        <div className="pt-2 space-y-4">
          <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// SHIPPING ADDRESS</p>
          <div>
            <AuthLabel>STREET ADDRESS</AuthLabel>
            <AuthInput type="text" value={line1} onChange={(e) => setLine1(e.target.value)} required data-testid="register-line1-input" />
          </div>
          <div>
            <AuthLabel>APT / SUITE (OPTIONAL)</AuthLabel>
            <AuthInput type="text" value={line2} onChange={(e) => setLine2(e.target.value)} data-testid="register-line2-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <AuthLabel>CITY</AuthLabel>
              <AuthInput type="text" value={city} onChange={(e) => setCity(e.target.value)} required data-testid="register-city-input" />
            </div>
            <div>
              <AuthLabel>STATE</AuthLabel>
              <AuthInput type="text" value={state} onChange={(e) => setState(e.target.value)} required data-testid="register-state-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <AuthLabel>POSTAL CODE</AuthLabel>
              <AuthInput type="text" value={postal} onChange={(e) => setPostal(e.target.value)} required data-testid="register-postal-input" />
            </div>
            <div>
              <AuthLabel>COUNTRY</AuthLabel>
              <AuthInput type="text" value={country} onChange={(e) => setCountry(e.target.value)} required data-testid="register-country-input" />
            </div>
          </div>
        </div>

        <AuthErrorLine error={error} />
        <AuthPrimaryButton type="submit" loading={loading} disabled={!complete} data-testid="register-submit-btn">
          CREATE ACCOUNT <ArrowRight size={16} />
        </AuthPrimaryButton>
        <div className="flex items-center gap-3 my-2 text-off-white/30">
          <div className="flex-1 h-px bg-off-white/10" />
          <span className="font-mono text-[10px] tracking-[0.25em]">OR</span>
          <div className="flex-1 h-px bg-off-white/10" />
        </div>
        <GoogleSignInButton />
        <p className="font-mono text-[9px] tracking-[0.25em] text-off-white/40 text-center pt-2">// GOOGLE USERS WILL BE ASKED FOR AN ADDRESS AFTER SIGNING IN.</p>
      </form>
    </AuthShell>
  );
};

export default Register;
